import {
  EventMessageUtils,
  EventType,
  InteractionRequiredAuthError,
  InteractionStatus,
  InteractionType,
} from '@azure/msal-browser';
import type { AccountInfo, AuthenticationResult, IPublicClientApplication, RedirectRequest } from '@azure/msal-browser';
import { clearRedirectClientId, storeRedirectClientId } from '@fjuel/msal/clientIdStorage';
import { loginRequest } from '@fjuel/msal/msal.config';
import { getMsalInstance } from '@fjuel/msal/msalInstance';
import { trackException } from '@fjuel/observability/applicationInsights';

export class MsalInteractionInProgressError extends Error {
  constructor() {
    super('An MSAL interaction is already in progress');
    this.name = 'MsalInteractionInProgressError';
  }
}

export type AuthStage = 'token' | 'login' | 'logout' | 'bootstrap' | 'claims';
export type AuthOutcome =
  | { type: 'token'; result: AuthenticationResult }
  | { type: 'error'; accountKey: string | null; error: Error; stage: AuthStage };

const listeners = new Set<(outcome: AuthOutcome) => void>();
const inFlightTokenRequests = new Map<string, Promise<string>>();
let interactionStatus: InteractionStatus = InteractionStatus.None;
let interactivePromise: Promise<void> | undefined;
let interactiveStage: 'login' | 'logout' | undefined;
let sessionGeneration = 0;

const invalidateSession = (): void => {
  sessionGeneration++;
};

export const getAccountKey = (account: AccountInfo | null): string | null =>
  account
    ? JSON.stringify([account.homeAccountId, account.localAccountId, account.tenantId, account.environment])
    : null;

export const getAuthErrorDetails = (error: unknown): { code: string; suberror: string; correlationId: string } => ({
  code:
    error instanceof Error && 'errorCode' in error && typeof error.errorCode === 'string'
      ? error.errorCode
      : 'authentication_error',
  suberror: error instanceof Error && 'subError' in error && typeof error.subError === 'string' ? error.subError : '',
  correlationId:
    error instanceof Error && 'correlationId' in error && typeof error.correlationId === 'string'
      ? error.correlationId
      : '',
});

export const isExtensionTimeout = (error: unknown): boolean =>
  error instanceof Error && /\b1003005\b/.test(error.message);

export const isTransientAuthError = (error: unknown): boolean =>
  [
    'network_error',
    'no_network_connectivity',
    'post_request_failed',
    'server_error',
    'temporarily_unavailable',
  ].includes(getAuthErrorDetails(error).code);

export const canRetainAuthClaims = (error: unknown): boolean =>
  isTransientAuthError(error) ||
  isExtensionTimeout(error) ||
  ['timed_out', 'monitor_window_timeout'].includes(getAuthErrorDetails(error).code);

export const reportAuthFailure = (error: unknown, stage: AuthStage): Error => {
  const details = getAuthErrorDetails(error);
  const safeError = new Error(
    isExtensionTimeout(error)
      ? 'The identity provider authentication extension timed out (1003005).'
      : `Authentication failed during ${stage} (${details.code}).`
  );
  trackException(safeError, { stage, ...details });
  return error instanceof Error ? error : safeError;
};

const publish = (outcome: AuthOutcome): void => listeners.forEach((listener) => listener(outcome));

export const subscribeAuthOutcomes = (listener: (outcome: AuthOutcome) => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const reportOperationFailure = (error: unknown, stage: AuthStage, accountKey: string | null): Error => {
  const failure = reportAuthFailure(error, stage);
  publish({ type: 'error', error: failure, stage, accountKey });
  return failure;
};

export const isAuthInteractionPending = (): boolean =>
  interactivePromise !== undefined || interactionStatus !== InteractionStatus.None;

export const observeMsalInteractions = (instance: IPublicClientApplication): void => {
  interactionStatus = InteractionStatus.None;
  interactivePromise = undefined;
  interactiveStage = undefined;
  invalidateSession();
  instance.addEventCallback((event) => {
    if (
      event.eventType === EventType.LOGOUT_START ||
      event.eventType === EventType.ACTIVE_ACCOUNT_CHANGED ||
      (event.eventType === EventType.ACQUIRE_TOKEN_START &&
        (event.interactionType === InteractionType.Redirect || event.interactionType === InteractionType.Popup))
    ) {
      invalidateSession();
    }
    if (event.eventType === EventType.RESTORE_FROM_BFCACHE) {
      interactivePromise = undefined;
      interactiveStage = undefined;
    }
    const nextStatus = EventMessageUtils.getInteractionStatusFromEvent(event, interactionStatus);
    if (nextStatus !== null) {
      interactionStatus = nextStatus;
    }
  });
};

export const coordinateAuthInteraction = (
  instance: IPublicClientApplication,
  stage: 'login' | 'logout',
  action: () => Promise<void>
): Promise<void> => {
  if (interactivePromise) {
    return interactiveStage === stage ? interactivePromise : Promise.reject(new MsalInteractionInProgressError());
  }
  if (interactionStatus !== InteractionStatus.None) {
    return Promise.reject(new MsalInteractionInProgressError());
  }
  invalidateSession();
  const operation: Promise<void> = Promise.resolve().then(async () => {
    try {
      if (stage === 'login') {
        storeRedirectClientId(instance.getConfiguration().auth.clientId);
      } else {
        clearRedirectClientId();
      }
      await action();
      if (stage === 'logout' && interactivePromise === operation) {
        interactivePromise = undefined;
        interactiveStage = undefined;
      }
    } catch (error: unknown) {
      clearRedirectClientId();
      if (interactivePromise === operation) {
        interactivePromise = undefined;
        interactiveStage = undefined;
      }
      throw reportOperationFailure(error, stage, getAccountKey(instance.getActiveAccount()));
    }
  });
  interactivePromise = operation;
  interactiveStage = stage;
  return operation;
};

export const signIn = (
  instance: IPublicClientApplication = getMsalInstance(),
  request?: Pick<RedirectRequest, 'redirectStartPage'>
): Promise<void> =>
  coordinateAuthInteraction(instance, 'login', () =>
    instance.loginRedirect({
      ...loginRequest,
      ...request,
      prompt: 'select_account',
    })
  );

export async function getAccessToken(): Promise<string> {
  const instance = getMsalInstance();
  const account = instance.getActiveAccount();
  const generation = sessionGeneration;
  const key = JSON.stringify([
    generation,
    instance.getConfiguration().auth.clientId,
    getAccountKey(account),
    [...loginRequest.scopes].sort(),
  ]);
  const existing = inFlightTokenRequests.get(key);
  if (existing) {
    return existing;
  }
  if (isAuthInteractionPending()) {
    throw new MsalInteractionInProgressError();
  }
  const operation: Promise<string> = Promise.resolve().then(async (): Promise<string> => {
    if (!account) {
      await signIn(instance);
      throw new MsalInteractionInProgressError();
    }
    const targetAccount = account;
    try {
      const result = await instance.acquireTokenSilent({ ...loginRequest, account: targetAccount });
      if (
        generation !== sessionGeneration ||
        getAccountKey(instance.getActiveAccount()) !== getAccountKey(result.account)
      ) {
        throw new MsalInteractionInProgressError();
      }
      if (!result.accessToken) {
        throw new Error('No API access token was returned');
      }
      publish({ type: 'token', result });
      return result.accessToken;
    } catch (error: unknown) {
      if (generation !== sessionGeneration) {
        throw new MsalInteractionInProgressError();
      }
      if (error instanceof MsalInteractionInProgressError) {
        throw error;
      }
      if (error instanceof InteractionRequiredAuthError) {
        await coordinateAuthInteraction(instance, 'login', () =>
          instance.acquireTokenRedirect({
            ...loginRequest,
            prompt: 'select_account',
            account: targetAccount,
          })
        );
        throw new MsalInteractionInProgressError();
      }
      throw reportOperationFailure(error, 'token', getAccountKey(targetAccount));
    }
  });
  inFlightTokenRequests.set(key, operation);
  try {
    return await operation;
  } finally {
    if (inFlightTokenRequests.get(key) === operation) {
      inFlightTokenRequests.delete(key);
    }
  }
}
